import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, catchError, of } from 'rxjs';
import { Product } from '@core/models';
import { ProductService } from './product.service';

const STORAGE_KEY = 'ecommerce_favorites_v1';

/** Favoritos del navegador: solo IDs. Los datos del producto salen del catálogo real. */
@Injectable({ providedIn: 'root' })
export class FavoriteService {
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private products = inject(ProductService);
  private ids = signal<string[]>(this.load());

  readonly productIds = this.ids.asReadonly();
  readonly count = computed(() => this.ids().length);

  has(productId: string): boolean {
    return this.ids().includes(productId);
  }

  toggle(productId: string): void {
    this.ids.update((list) => (list.includes(productId) ? list.filter((id) => id !== productId) : [...list, productId]));
    this.persist();
  }

  remove(productId: string): void {
    this.ids.update((list) => list.filter((id) => id !== productId));
    this.persist();
  }

  /**
   * Los favoritos guardados se resuelven contra el catálogo visible de la tienda actual: un
   * producto despublicado, o guardado en otra tienda, simplemente deja de aparecer.
   */
  getFavoriteProducts(): Observable<Product[]> {
    const ids = this.ids();
    if (!ids.length) return of([]);
    return this.products.getByIds(ids).pipe(catchError(() => of([])));
  }

  private load(): string[] {
    if (!this.isBrowser) return [];
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  private persist(): void {
    if (!this.isBrowser) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.ids()));
    } catch {
      /* localStorage no disponible */
    }
  }
}
